import type { SurfaceDetailDefinition } from '@helios/ui';

const EXCLUDED =
  'form, input, select, textarea, script, style, svg, canvas, [hidden], [aria-hidden="true"], .sr-only, [role="tooltip"], .recharts-tooltip-wrapper, [data-surface-inspect-control], [data-surface-inspect-marker], [data-surface-detail-data]';
const ROW_LIMIT = 500;
const clean = (value: string | null | undefined) => value?.replace(/\s+/g, ' ').trim() ?? '';

export interface SurfaceTable {
  caption: string;
  columns: string[];
  rows: string[][];
  omittedRows: number;
}
export interface SurfaceSnapshot {
  title: string;
  context: string[];
  tables: SurfaceTable[];
  links: { label: string; href: string }[];
  detail?: SurfaceDetailDefinition;
}
function textOf(element: Element): string {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const excluded = node.parentElement?.closest(EXCLUDED);
      return (excluded && element.contains(excluded)) || hiddenWithin(element, node.parentElement)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT;
    },
  });
  const parts: string[] = [];
  while (walker.nextNode()) {
    const text = clean(walker.currentNode.textContent);
    if (text) parts.push(text);
  }
  return clean(parts.join(' '));
}

function hiddenWithin(source: Element, element: Element | null): boolean {
  for (
    let current = element;
    current && source.contains(current);
    current = current.parentElement
  ) {
    if (current.matches('[hidden], [aria-hidden="true"], .sr-only')) return true;
    const style = window.getComputedStyle(current);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse' ||
      style.contentVisibility === 'hidden'
    )
      return true;
    if (current === source) break;
  }
  return false;
}

function excludedWithin(source: HTMLElement, element: Element, selector: string) {
  const excluded = element.closest(selector);
  return (excluded !== null && source.contains(excluded)) || hiddenWithin(source, element);
}

function safeLink(href: string): string | null {
  try {
    const url = new URL(href, window.location.href);
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : url.href;
  } catch {
    return null;
  }
}

/** Read only the already-authorized rendered source; never copy form values or HTML. */
export function snapshotDataSurface(
  source: HTMLElement,
  detail?: SurfaceDetailDefinition,
): SurfaceSnapshot {
  // Keep a point-in-time inert copy. A caller mutating a loaded array in place
  // must not silently change the already-open dialog without its source fence.
  const detailCopy = detail
    ? {
        title: detail.title,
        description: detail.description,
        sections: detail.sections?.map((section) => ({
          title: section.title,
          description: section.description,
          fields: section.fields?.map((field) => ({ label: field.label, value: field.value })),
          columns: section.columns ? [...section.columns] : undefined,
          rows: section.rows?.map((row) => [...row]),
        })),
        links: detail.links?.flatMap((link) => {
          const href = safeLink(link.href);
          return href ? [{ label: link.label, href }] : [];
        }),
      }
    : undefined;
  const labelledBy = source.getAttribute('aria-labelledby');
  const labelledElement = labelledBy ? document.getElementById(labelledBy) : null;
  const heading = Array.from(source.querySelectorAll('h1, h2, h3, h4, h5, h6')).find(
    (element) => !hiddenWithin(source, element),
  );
  const firstParagraph = Array.from(source.querySelectorAll('p')).find(
    (element) => !excludedWithin(source, element, EXCLUDED),
  );
  const title =
    detail?.title ||
    clean(source.dataset.surfaceTitle) ||
    clean(source.getAttribute('aria-label')) ||
    (labelledElement && source.contains(labelledElement) && !hiddenWithin(source, labelledElement)
      ? textOf(labelledElement)
      : '') ||
    (heading ? textOf(heading) : '') ||
    (firstParagraph ? textOf(firstParagraph) : '') ||
    'Data panel';
  const tables = Array.from(source.querySelectorAll('table'))
    .filter((table) => !excludedWithin(source, table, '[hidden], [aria-hidden="true"], form'))
    .map((table): SurfaceTable => {
      const body = Array.from(table.querySelectorAll('tbody tr')).filter(
        (row) => !hiddenWithin(source, row),
      );
      const caption = table.querySelector('caption');
      return {
        caption: caption && !hiddenWithin(source, caption) ? textOf(caption) : '',
        columns: Array.from(
          table.querySelectorAll('thead tr:last-child th, thead tr:last-child td'),
        )
          .filter((cell) => !hiddenWithin(source, cell))
          .map(textOf),
        rows: body.slice(0, ROW_LIMIT).map((row) =>
          Array.from(row.children)
            .filter((cell) => !hiddenWithin(source, cell))
            .map(textOf),
        ),
        omittedRows: Math.max(0, body.length - ROW_LIMIT),
      };
    });
  const context = Array.from(source.querySelectorAll('p, dt, dd, li, [data-detail-field]'))
    .filter((element) => !excludedWithin(source, element, `${EXCLUDED}, table, button, a`))
    .map(textOf)
    .filter((text) => text && text !== title);
  if (!context.length && !tables.length) {
    const text = textOf(source);
    if (text && text !== title) context.push(text);
  }
  const links = Array.from(source.querySelectorAll<HTMLAnchorElement>('a[href]'))
    .filter((link) => !excludedWithin(source, link, '[hidden], [aria-hidden="true"], form'))
    .flatMap((link) => {
      const href = safeLink(link.href);
      const label = textOf(link) || clean(link.getAttribute('aria-label'));
      return href && label ? [{ label, href }] : [];
    });
  for (const link of detail?.links ?? []) {
    const href = safeLink(link.href);
    if (href) links.push({ label: link.label, href });
  }
  return {
    title,
    context: [...new Set(context)],
    tables,
    links: links.filter(
      (link, index, rows) =>
        rows.findIndex((other) => other.href === link.href && other.label === link.label) === index,
    ),
    detail: detailCopy,
  };
}
